import { FC, memo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import { Request } from 'commons/utils/request/request'
import { Accordion, DownloadLink, Toast } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { IconButton, OutlineButton } from 'ui/components/Buttons'
import { DateTime } from 'ui/components/DateTime'
import { Icon, IconName } from 'ui/components/Icon'
import { useConfirmation } from 'ui/hooks'
import { DotLabelBadge } from 'ui/temp/DotLabelBadge'

import { useSelectionPublicationContext } from '../../../../context/SelectionPublicationContext'
import { PublicationProps } from './PublicationProps.types'
import { CRBMonitorLogo, CheckboxContainer, PublicationNotesDialog } from './components'

import { s } from './styles'

export const Publication: FC<PublicationProps> = memo(({ publication }) => {
  const router = useRouter()
  const { toggleId, isItemSelected } = useSelectionPublicationContext()
  const { needsReview, notes, publicationDate, showMatches, snippet, title, url, id, enableCRBMLogo, showAttachment } =
    publication
  const [isNotesOpened, setIsNotesOpened] = useState(false)

  const { mutate: reviewPublication } = useMutation<void, Error, { ids: number[] }>({
    mutationFn: (opts) => Request.patch(Request.urls.bank.adverseMediaPublicationReview, opts),
    onSuccess: async () => {
      router.invalidate()
    },
    onError: (e) => {
      Toast.errorSmall(e.message)
    }
  })

  const { mutate: unreviewPublication } = useMutation<void, Error, { ids: number[] }>({
    mutationFn: (opts) => Request.patch(Request.urls.bank.adverseMediaPublicationUnreview, opts),
    onSuccess: async () => {
      router.invalidate()
    },
    onError: (e) => {
      Toast.errorSmall(e.message)
    }
  })

  const { open: openUnreviewConfirmation, Confirmation: UnreviewConfirmation } = useConfirmation({
    message: 'Are you sure you want to make this article unreviewed?',
    onConfirm: () => unreviewPublication({ ids: [id] }),
    confirmationButtonText: 'Unreview',
    isConfirmationNegative: true
  })

  return (
    <>
      <UnreviewConfirmation />
      {isNotesOpened && <PublicationNotesDialog publication={publication} onClose={() => setIsNotesOpened(false)} />}
      <s.PublicationWrapper needsReview={needsReview}>
        {needsReview && (
          <s.WrappedCheckBoxContainer>
            <CheckboxContainer />
            <s.WrappedCheckBox checkBoxLabel="" value={isItemSelected(id)} onChange={() => toggleId(id)} />
          </s.WrappedCheckBoxContainer>
        )}
        <s.Header>
          <s.HeaderLeft>
            <s.DateTimeWrapper>
              <DateTime date={publicationDate} />
            </s.DateTimeWrapper>
            {url && (
              <Link to={url} target="_blank">
                <Icon name={IconName.LINK_GREEN} />
              </Link>
            )}
            {enableCRBMLogo && <CRBMonitorLogo />}
          </s.HeaderLeft>

          <ButtonGroup margin="big">
            {showAttachment && (
              <DownloadLink
                direct
                parametersForm={{ publicationId: id }}
                name={showAttachment.fileName}
                baseUrl="/bank/v1/adverse-media-publication/download-attachment"
                handleError={() => {}}
              >
                <s.LongFilename title={showAttachment.fileName}>{showAttachment.fileName}</s.LongFilename>
              </DownloadLink>
            )}
            <IconButton
              face="positive"
              icon={notes ? IconName.DIALOG_FILLED : IconName.DIALOG}
              onClick={() => setIsNotesOpened(true)}
            >
              Notes
            </IconButton>
            {needsReview ? (
              <OutlineButton onClick={() => reviewPublication({ ids: [id] })} face="positive">
                Review
              </OutlineButton>
            ) : (
              <OutlineButton icon={IconName.CHECK} face="neutral" onClick={() => openUnreviewConfirmation()}>
                Reviewed
              </OutlineButton>
            )}
          </ButtonGroup>
        </s.Header>
        <s.Title tabIndex={0}>{title}</s.Title>
        {snippet && <Accordion title="Details" bottomPadding="small" children={<p>{snippet}</p>} />}
        {showMatches && (
          <Accordion
            title="Matches"
            bottomPadding="small"
            children={
              <s.AccordionWrapper>
                {showMatches.map((match) => (
                  <DotLabelBadge key={match} dotActive={true} label={match} />
                ))}
              </s.AccordionWrapper>
            }
          />
        )}
      </s.PublicationWrapper>
    </>
  )
})
